import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { contentfulApiArticleToArticleProps } from '../../transformers/contentfulApiArticleToArticleProps';
import { useContentMetafieldData } from '@jbhi-fi/components/src/hooks/useContentMetafieldData/useContentMetafieldData';
import { ArticleProps } from '../../views/Article/Article';
import { contentfulApiArticleToStackSchemaProps } from '../../transformers/stackSchema/contentfulApiArticleToStackSchemaProps';
import { StackSchema, StackSchemaProps } from '../../components/StackSchema/StackSchema';
import { ContentfulArticle, EntryType } from '@jbhi-fi/apis/src/contentful/types';
import { UseContentProps } from '@jbhi-fi/components/src/types';
import { useContentfulEntries } from '@jbhi-fi/components/src/hooks/useContentfulEntries/useContentfulEntries';

export const ArticlePropsContext = createContext<UseContentProps<ArticleProps> | null>(null);

export const ArticlePropsProvider = ({ children }: { children: ReactNode }) => {
  const metafieldData = useContentMetafieldData<ContentfulArticle>();
  const { entries, isLoading, error } = useContentfulEntries<ContentfulArticle>({
    filter: {
      content_type: EntryType.article,
      'sys.id': metafieldData?.sys?.id ?? '',
    },
    autoRefresh: window.Jbhifi.isContentPreview,
  });

  const data = entries?.length ? entries[0] : null;
  const [article, setArticle] = useState<ArticleProps>(contentfulApiArticleToArticleProps(metafieldData));

  useEffect(() => {
    setArticle(contentfulApiArticleToArticleProps(data || metafieldData));
  }, [data]);

  return (
    <ArticlePropsContext.Provider value={{ data: article, isLoading, error }}>
      {data?.fields ? <StackSchema {...(contentfulApiArticleToStackSchemaProps(data) as StackSchemaProps)} /> : null}
      {children}
    </ArticlePropsContext.Provider>
  );
};

export const useArticleProps = (): UseContentProps<ArticleProps> => {
  const context = useContext(ArticlePropsContext);
  if (context === undefined || context === null) {
    throw new Error('useArticle must be used within a ArticleProvider');
  }

  return context;
};
